import {
  AvailabilityException,
  ResolvedDay,
  TimeRange,
  WeeklyDay,
} from "@/interfaces/Availability";
import { DateTime } from "luxon";

export const MAX_RANGES_PER_DAY = 8;
export const MAX_EXCEPTION_DAYS = 366;
export const WEEKDAY_LABELS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const pad = (n: number) => String(n).padStart(2, "0");

export const toMinutes = (value: string) => {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
};

export const fromMinutes = (minutes: number) =>
  `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/** Quarts d'heure : un début va de 00:00 à 23:45, une fin de 00:15 à 24:00. */
const QUARTER_HOURS = Array.from({ length: 97 }, (_, i) => fromMinutes(i * 15));
export const START_OPTIONS = QUARTER_HOURS.slice(0, -1);
export const END_OPTIONS = QUARTER_HOURS.slice(1);

export const emptyWeek = (): WeeklyDay[] =>
  [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({ weekday, ranges: [] }));

/** Journée type de l'entreprise : celle qu'on propose quand on ouvre un jour. */
export const DEFAULT_RANGE: TimeRange = { start: "09:00", end: "18:00" };

/** Plage ajoutée : la journée type pour la première, sinon une heure à la suite. `null` si la journée est pleine. */
export const nextRange = (ranges: TimeRange[]): TimeRange | null => {
  if (ranges.length >= MAX_RANGES_PER_DAY) return null;
  const last = ranges[ranges.length - 1];
  if (!last) return { ...DEFAULT_RANGE };
  const start = toMinutes(last.end);
  if (start >= 1440) return null;
  return { start: last.end, end: fromMinutes(Math.min(start + 60, 1440)) };
};

/**
 * Une journée, priorité exception > férié > semaine type : la même règle que
 * l'API (spec 2026-10-08, § 2.2), pour l'aperçu des modifications non
 * enregistrées. L'API reste l'autorité.
 */
export const resolveDay = (
  weekly: WeeklyDay[],
  exceptions: AvailabilityException[],
  date: string,
  holidayName?: string,
): ResolvedDay => {
  const exception = exceptions.find(
    (e) => e.startDate <= date && date <= e.endDate,
  );
  if (exception) {
    const ranges = exception.isOff ? [] : exception.ranges;
    return {
      date,
      source: "exception",
      isOff: ranges.length === 0,
      holidayName: holidayName ?? null,
      ranges,
    };
  }
  if (holidayName) {
    return { date, source: "holiday", isOff: true, holidayName, ranges: [] };
  }
  const weekday = DateTime.fromISO(date, { zone: "utc" }).weekday;
  const ranges = weekly.find((d) => d.weekday === weekday)?.ranges ?? [];
  return { date, source: "weekly", isOff: ranges.length === 0, holidayName: null, ranges };
};

/** Libellé court d'une journée : ses plages, ou pourquoi elle est fermée. */
export const dayLabel = (day: ResolvedDay): string => {
  if (day.source === "unset") return "Non renseignées";
  if (day.ranges.length) {
    return day.ranges.map((r) => `${r.start}–${r.end}`).join(" · ");
  }
  if (day.source === "exception") return "Congé";
  if (day.source === "holiday") return "Férié";
  return "Fermé";
};

export const fitsInRanges = (ranges: TimeRange[], start: number, end: number) => {
  const merged: { start: number; end: number }[] = [];
  for (const range of [...ranges]
    .map((r) => ({ start: toMinutes(r.start), end: toMinutes(r.end) }))
    .sort((a, b) => a.start - b.start)) {
    const last = merged[merged.length - 1];
    if (last && range.start <= last.end) last.end = Math.max(last.end, range.end);
    else merged.push(range);
  }
  return merged.some((range) => range.start <= start && end <= range.end);
};

const rangeErrors = (
  ranges: TimeRange[],
  field: string,
  errors: Record<string, string>,
) => {
  if (ranges.length > MAX_RANGES_PER_DAY) {
    errors[field] = `${MAX_RANGES_PER_DAY} plages au plus par jour.`;
    return;
  }
  ranges.forEach((range, index) => {
    const start = toMinutes(range.start);
    const end = toMinutes(range.end);
    if (start >= end) {
      errors[`${field}.${index}`] = "La fin doit suivre le début.";
    } else if (index > 0 && toMinutes(ranges[index - 1].end) > start) {
      errors[`${field}.${index}`] =
        "Les plages doivent être ordonnées et ne pas se chevaucher.";
    }
  });
};

/** Erreurs de saisie par champ : mêmes règles et mêmes messages que l'API (`scheduleRules.ts`). */
export const scheduleErrors = (
  weekly: WeeklyDay[],
  exceptions: AvailabilityException[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  weekly.forEach((day, index) =>
    rangeErrors(day.ranges, `weekly.${index}.ranges`, errors),
  );

  const periods: { index: number; start: DateTime; end: DateTime }[] = [];
  exceptions.forEach((exception, index) => {
    const field = `exceptions.${index}`;
    const start = DateTime.fromISO(exception.startDate, { zone: "utc" });
    const end = DateTime.fromISO(exception.endDate, { zone: "utc" });
    if (!start.isValid || !end.isValid) {
      errors[field] = "Date invalide.";
      return;
    }
    if (end < start) {
      errors[field] = "La date de fin doit suivre la date de début.";
      return;
    }
    if (end.diff(start, "days").days + 1 > MAX_EXCEPTION_DAYS) {
      errors[field] = `${MAX_EXCEPTION_DAYS} jours au plus par période.`;
    }
    if (!exception.isOff) {
      if (exception.ranges.length === 0) {
        errors[`${field}.ranges`] =
          "Ajoutez au moins une plage horaire, ou choisissez « Congé ».";
      } else {
        rangeErrors(exception.ranges, `${field}.ranges`, errors);
      }
    }
    periods.push({ index, start, end });
  });

  periods.sort((a, b) => a.start.toMillis() - b.start.toMillis());
  // Maximum courant des fins : une longue exception peut en contenir plusieurs.
  let maxEnd: DateTime | null = null;
  for (const period of periods) {
    if (maxEnd && period.start <= maxEnd) {
      const field = `exceptions.${period.index}`;
      if (!errors[field]) {
        errors[field] = "Ces dates chevauchent un autre congé ou des horaires particuliers.";
      }
    }
    if (!maxEnd || period.end > maxEnd) maxEnd = period.end;
  }
  return errors;
};

/** « d'Avner », « de Cindy ». */
export const ofName = (firstname: string) =>
  /^[aeiouyhàâäéèêëîïôöûüù]/i.test(firstname)
    ? `d'${firstname}`
    : `de ${firstname}`;

/** « 09:30 » → « 9h30 », « 18:00 » → « 18h » : pour les cases étroites de l'aperçu. */
export const compactTime = (value: string) => {
  const [hours, minutes] = value.split(":");
  return `${Number(hours)}h${minutes === "00" ? "" : minutes}`;
};

/** L'amplitude d'une journée en version courte : « 9h–18h ». */
export const compactSpan = (ranges: TimeRange[]) =>
  ranges.length
    ? `${compactTime(ranges[0].start)}–${compactTime(ranges[ranges.length - 1].end)}`
    : "";

/**
 * Date proposée pour un nouveau congé : le prochain jour ouvert de la semaine
 * type après `today` (un congé un samedi déjà fermé ne sert à rien), à défaut
 * demain.
 */
export const nextOpenDate = (weekly: WeeklyDay[], today: string): string => {
  const start = DateTime.fromISO(today, { zone: "utc" });
  for (let offset = 1; offset <= 14; offset++) {
    const candidate = start.plus({ days: offset });
    const day = weekly.find((d) => d.weekday === candidate.weekday);
    if (day?.ranges.length) return candidate.toISODate()!;
  }
  return start.plus({ days: 1 }).toISODate()!;
};

/** Plus court encore, pour les cases d'un écran de téléphone : « 9–18 », « 8:30–17 ». */
export const shortSpan = (ranges: TimeRange[]) => {
  if (!ranges.length) return "";
  const short = (value: string) => {
    const [hours, minutes] = value.split(":");
    return minutes === "00" ? `${Number(hours)}` : `${Number(hours)}:${minutes}`;
  };
  return `${short(ranges[0].start)}–${short(ranges[ranges.length - 1].end)}`;
};
